import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { FaTrashAlt, FaCheckCircle, FaShieldAlt, FaUserTimes, FaHistory, FaCog, FaCrown } from 'react-icons/fa';
import { HiMail, HiExclamation } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import Calling from '../api/Calling';
import Logo from '../Image/Client/Logo.svg';

const AccountDeleteRequest = () => {
    const [identifier, setIdentifier] = useState('');
    const [loader, setLoader] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [confirmCheck, setConfirmCheck] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!identifier.trim()) {
            toast.error('Please enter your username or email');
            return;
        }

        if (!confirmCheck) {
            toast.error('Please confirm that you understand the consequences');
            return;
        }

        setLoader(true);
        try {
            const response = await Calling.sendRequestDelete({ identifier: identifier.trim() });
            if (response?.status) {
                toast.success(response?.message || 'Delete request submitted successfully');
                setSubmitted(true);
            } else {
                toast.error(response?.message || 'Failed to submit request');
            }
        } catch (error) {
            console.error('Error submitting delete request:', error);
            toast.error('Something went wrong. Please try again.');
        } finally {
            setLoader(false);
        }
    };

    const deleteItems = [
        { icon: FaUserTimes, text: 'Profile & account information' },
        { icon: FaHistory, text: 'Activity and call history' },
        { icon: FaCog, text: 'Preferences and settings' },
        { icon: FaCrown, text: 'Active subscriptions or plans' },
    ];

    // Success State
    if (submitted) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4 w-full">
                <div className="bg-white rounded-3xl shadow-2xl shadow-slate-200/50 max-w-md w-full p-8 text-center">
                    {/* Success Icon */}
                    <div className="relative mx-auto w-20 h-20 mb-6">
                        <div className="absolute inset-0 bg-emerald-500/20 rounded-full animate-ping" />
                        <div className="relative w-20 h-20 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center shadow-lg shadow-emerald-500/30">
                            <FaCheckCircle className="text-white text-3xl" />
                        </div>
                    </div>

                    <h2 className="text-2xl font-bold text-slate-800 mb-3">Request Submitted!</h2>
                    <p className="text-slate-500 mb-6 leading-relaxed">
                        Your account deletion request has been received. We'll process it within
                        <span className="font-semibold text-slate-700"> 30 days</span>.
                    </p>

                    <div className="bg-slate-50 rounded-2xl p-4 mb-6">
                        <p className="text-sm text-slate-600">
                            📧 You'll receive a confirmation email once your account is deleted.
                        </p>
                    </div>

                    <button
                        onClick={() => navigate('/')}
                        className="w-full py-3.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-blue-500/30 transition-all hover:-translate-y-0.5 active:translate-y-0"
                    >
                        Back to Home
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col w-full">
            {/* Header */}
            <header className="w-full py-4 px-4 sm:px-6 bg-white/70 backdrop-blur-lg border-b border-slate-200/50 sticky top-0 z-10">
                <div className="max-w-6xl mx-auto flex items-center justify-between">
                    <button
                        onClick={() => navigate('/')}
                        className="flex items-center gap-2.5 hover:opacity-80 transition-opacity"
                    >
                        <img src={Logo} alt="Caller Name" className="h-10 w-10" />
                        <span className="text-lg font-bold text-slate-800">Caller Name</span>
                    </button>
                    <div className="flex items-center gap-1.5 text-slate-500 text-sm bg-slate-100 px-3 py-1.5 rounded-full">
                        <FaShieldAlt className="text-blue-500 text-xs" />
                        <span className="hidden sm:inline text-xs font-medium">Privacy First</span>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="flex-1 flex items-center justify-center p-4 sm:p-6">
                <div className="w-full max-w-lg">
                    {/* Card */}
                    <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 overflow-hidden">
                        {/* Header */}
                        <div className="bg-gradient-to-r from-red-500 to-rose-500 px-6 py-5 flex items-center gap-4">
                            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                                <FaTrashAlt className="text-white text-lg" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-white">Delete Account</h1>
                                <p className="text-red-100 text-sm">This action cannot be undone</p>
                            </div>
                        </div>

                        {/* Body */}
                        <div className="p-6">
                            {/* Warning Alert */}
                            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6">
                                <div className="w-8 h-8 bg-amber-400 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <HiExclamation className="text-white text-lg" />
                                </div>
                                <p className="text-amber-800 text-sm leading-relaxed">
                                    <span className="font-semibold">Warning:</span> All your data will be permanently deleted and cannot be recovered.
                                </p>
                            </div>

                            {/* What will be deleted */}
                            <div className="mb-6">
                                <h3 className="text-sm font-semibold text-slate-700 mb-3">What will be deleted:</h3>
                                <div className="grid grid-cols-2 gap-2">
                                    {deleteItems.map((item, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center gap-2.5 bg-slate-50 rounded-xl px-3 py-2.5"
                                        >
                                            <item.icon className="text-red-400 text-sm flex-shrink-0" />
                                            <span className="text-xs text-slate-600 leading-tight">{item.text}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Form */}
                            <form onSubmit={handleSubmit}>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        Verify your identity
                                    </label>
                                    <input
                                        type="text"
                                        value={identifier}
                                        onChange={(e) => setIdentifier(e.target.value)}
                                        placeholder="Enter username or email"
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-red-500/20 focus:border-red-400 outline-none transition-all text-sm placeholder:text-slate-400"
                                        disabled={loader}
                                    />
                                </div>

                                {/* Checkbox */}
                                <label className="flex items-start gap-3 cursor-pointer mb-6 group">
                                    <div className="relative mt-0.5">
                                        <input
                                            type="checkbox"
                                            checked={confirmCheck}
                                            onChange={(e) => setConfirmCheck(e.target.checked)}
                                            className="sr-only peer"
                                            disabled={loader}
                                        />
                                        <div className="w-5 h-5 border-2 border-slate-300 rounded-md peer-checked:border-red-500 peer-checked:bg-red-500 transition-all flex items-center justify-center group-hover:border-slate-400">
                                            {confirmCheck && (
                                                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                </svg>
                                            )}
                                        </div>
                                    </div>
                                    <span className="text-sm text-slate-500 leading-relaxed">
                                        I understand this is <span className="font-medium text-slate-700">permanent</span> and my data will be deleted within 30 days.
                                    </span>
                                </label>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={loader || !confirmCheck}
                                    className={`w-full py-3.5 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${loader || !confirmCheck
                                        ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                        : 'bg-gradient-to-r from-red-500 to-rose-500 text-white hover:shadow-lg hover:shadow-red-500/25 hover:-translate-y-0.5 active:translate-y-0'
                                        }`}
                                >
                                    {loader ? (
                                        <>
                                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                            </svg>
                                            <span>Processing...</span>
                                        </>
                                    ) : (
                                        <>
                                            <FaTrashAlt className="text-sm" />
                                            <span>Submit Delete Request</span>
                                        </>
                                    )}
                                </button>
                            </form>
                        </div>
                    </div>

                    {/* Help Text */}
                    <p className="text-center text-xs text-slate-400 mt-4 px-4">
                        Changed your mind? Contact support before your request is processed.
                    </p>
                </div>
            </main>

            {/* Footer */}
            <footer className="w-full py-4 px-4 sm:px-6 bg-white border-t border-slate-100">
                <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-slate-400 text-xs">
                        <img src={Logo} alt="" className="h-5 w-5 opacity-50" />
                        <span>© {new Date().getFullYear()} Vedicant Technology</span>
                    </div>
                    <a
                        href="mailto:contact@vedicantechnology.com"
                        className="text-slate-400 hover:text-blue-500 transition-colors text-xs flex items-center gap-1.5"
                    >
                        <HiMail className="w-3.5 h-3.5" />
                        contact@vedicantechnology.com
                    </a>
                </div>
            </footer>
        </div>
    );
};

export default AccountDeleteRequest;